import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../utils/api';
import { ToastContainer } from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CogIcon,
} from '@heroicons/react/24/outline';

interface AuditLogEntry {
  id: string;
  title: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'SYNC' | 'CLEANUP';
  objectType: string;
  objectId: string;
  objectName?: string;
  changes?: Record<string, unknown>;
  userId?: string;
  userEmail?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

interface FilterOptions {
  actions: string[];
  objectTypes: string[];
  userEmails: string[];
}

const LogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    actions: [],
    objectTypes: [],
    userEmails: [],
  });

  const [filters, setFilters] = useState({
    action: '',
    objectType: '',
    userEmail: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  });

  const { toasts, removeToast, showError } = useToast();

  const fetchFilterOptions = useCallback(async () => {
    try {
      const options = await api.getAuditLogFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getAuditLogs({
        page,
        limit,
        ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')),
      });
      setLogs(result.logs);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Error fetching audit logs:', (error as Error).message);
      showError('Failed to load logs', 'Could not fetch audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, showError]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedLog(null);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      objectType: '',
      userEmail: '',
      dateFrom: '',
      dateTo: '',
      search: '',
    });
    setPage(1);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'UPDATE':
        return <DocumentTextIcon className="h-5 w-5 text-blue-600" />;
      case 'DELETE':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case 'SYNC':
        return <ArrowPathIcon className="h-5 w-5 text-purple-600" />;
      case 'CLEANUP':
        return <CogIcon className="h-5 w-5 text-orange-600" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-base-content/70" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATE':
        return 'bg-green-100 text-green-800';
      case 'UPDATE':
        return 'bg-blue-100 text-blue-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      case 'SYNC':
        return 'bg-purple-100 text-purple-800';
      case 'CLEANUP':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  const hasActiveFilters = Object.values(filters).some((value) => value !== '');

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-base-content mb-2">Audit Logs</h1>
            <p className="text-base-content/70">Track all CRUD operations across your system</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                showFilters
                  ? 'bg-primary text-white'
                  : 'bg-base-200 text-base-content hover:bg-base-200'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {Object.values(filters).filter((v) => v !== '').length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-base-200 border border-base-300 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="action"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Action
                </label>
                <select
                  id="action"
                  name="action"
                  value={filters.action}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">All Actions</option>
                  {filterOptions.actions.map((action) => (
                    <option key={action} value={action}>
                      {action}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="objectType"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Object Type
                </label>
                <select
                  id="objectType"
                  name="objectType"
                  value={filters.objectType}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">All Types</option>
                  {filterOptions.objectTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="userEmail"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  User Email
                </label>
                <input
                  id="userEmail"
                  name="userEmail"
                  type="email"
                  value={filters.userEmail}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Filter by user email"
                />
              </div>

              <div>
                <label
                  htmlFor="dateFrom"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Date From
                </label>
                <input
                  id="dateFrom"
                  name="dateFrom"
                  type="date"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label
                  htmlFor="dateTo"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Date To
                </label>
                <input
                  id="dateTo"
                  name="dateTo"
                  type="date"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Search
                </label>
                <div className="relative">
                  <input
                    id="search"
                    name="search"
                    type="text"
                    value={filters.search}
                    onChange={handleFilterChange}
                    className="w-full pl-10 pr-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Search in titles, names, changes..."
                  />
                  <MagnifyingGlassIcon className="h-5 w-5 text-base-content/50 absolute left-3 top-2.5" />
                </div>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-base-content/70 hover:text-base-content underline"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Results Summary */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-base-content/70">
            Showing {logs.length} of {total} audit log{total !== 1 ? 's' : ''}
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="limit" className="text-sm text-base-content">
              Show:
            </label>
            <select
              id="limit"
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setPage(1);
              }}
              className="border border-base-300 rounded px-2 py-1 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Logs Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-base-300">
            <thead className="bg-base-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Action & Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Object
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <span className="ml-2 text-base-content/70">Loading audit logs...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-base-content/70">
                    {hasActiveFilters ? 'No logs match your filters.' : 'No audit logs found.'}
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const dateTime = formatDate(log.createdAt);
                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-base-200 cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {getActionIcon(log.action)}
                          <div className="ml-3">
                            <div className="text-sm font-medium text-base-content">{log.title}</div>
                            <div className="flex items-center mt-1">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}
                              >
                                {log.action}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-base-content capitalize">
                          {log.objectType.replace('_', ' ')}
                        </div>
                        {log.objectName && (
                          <div className="text-sm text-base-content/70 truncate max-w-xs">
                            {log.objectName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-base-content">{log.userEmail || 'System'}</div>
                        {log.ipAddress && (
                          <div className="text-sm text-base-content/70">{log.ipAddress}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-base-content">{dateTime.date}</div>
                        <div className="text-sm text-base-content/70">{dateTime.time}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary hover:text-primary/80">
                        View Details
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-base-content">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="inline-flex items-center px-3 py-2 border border-base-300 rounded-md bg-base-100 text-sm font-medium text-base-content/70 hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        pageNum === page
                          ? 'bg-primary text-white'
                          : 'bg-base-100 text-base-content hover:bg-base-200 border border-base-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="inline-flex items-center px-3 py-2 border border-base-300 rounded-md bg-base-100 text-sm font-medium text-base-content/70 hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto m-4 w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  {getActionIcon(selectedLog.action)}
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-base-content">{selectedLog.title}</h3>
                    <div className="flex items-center mt-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(selectedLog.action)}`}
                      >
                        {selectedLog.action}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-base-content/50 hover:text-base-content/70"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <h4 className="text-sm font-medium text-base-content mb-1">Object Details</h4>
                  <div className="text-sm text-base-content">Type: {selectedLog.objectType}</div>
                  <div className="text-sm text-base-content">ID: {selectedLog.objectId}</div>
                  {selectedLog.objectName && (
                    <div className="text-sm text-base-content">Name: {selectedLog.objectName}</div>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-base-content mb-1">User Information</h4>
                  <div className="text-sm text-base-content">
                    Email: {selectedLog.userEmail || 'System'}
                  </div>
                  {selectedLog.userId && (
                    <div className="text-sm text-base-content">User ID: {selectedLog.userId}</div>
                  )}
                  {selectedLog.ipAddress && (
                    <div className="text-sm text-base-content">
                      IP Address: {selectedLog.ipAddress}
                    </div>
                  )}
                  <div className="text-sm text-base-content">
                    Date: {formatDate(selectedLog.createdAt).date}{' '}
                    {formatDate(selectedLog.createdAt).time}
                  </div>
                </div>
              </div>

              {selectedLog.changes && (
                <div>
                  <h4 className="text-sm font-medium text-base-content mb-2">Changes</h4>
                  <div className="bg-base-200 rounded-lg p-4">
                    <pre className="text-xs text-base-content whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(selectedLog.changes, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 bg-neutral text-white rounded-md hover:bg-neutral/80 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default LogsPage;
